<template>
  <div class="h-full flex flex-col">
    <div class="bg-white border-b">
      <div class="p-3 md:p-4">
        <h1 class="text-xl md:text-2xl font-bold mb-4 text-center">Эксперты</h1>

        <!-- Поиск -->
        <div class="max-w-full mx-auto mb-4">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Поиск по имени или специализации"
            class="w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring focus:border-blue-300"
          />
        </div>

        <!-- Кнопка фильтров на мобильных -->
        <div class="md:hidden mb-3">
          <button
            @click="showFilters = !showFilters"
            class="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-md text-sm"
          >
            {{ showFilters ? '✕ Скрыть фильтры' : '⚙ Показать фильтры' }}
          </button>
        </div>

        <!-- Фильтры -->
        <div v-show="showFilters || !isMobile" class="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <!-- Статус пилота -->
        <div>
          <label class="block text-xs md:text-sm font-medium text-gray-700 mb-2">Статус пилота</label>
          <select
            v-model="filters.pilotStatus"
            class="w-full px-2 md:px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:border-blue-300"
          >
            <option value="">Все</option>
            <option value="pilot">Пилоты</option>
            <option value="non-pilot">Не пилоты</option>
          </select>
        </div>

        <!-- Категория пилота -->
        <div>
          <label class="block text-xs md:text-sm font-medium text-gray-700 mb-2">Категория пилота</label>
          <select
            v-model="filters.pilotCategory"
            class="w-full px-2 md:px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:border-blue-300"
          >
            <option value="">Все</option>
            <option value="first">Первая категория</option>
            <option value="second">Вторая категория</option>
            <option value="third">Третья категория</option>
          </select>
        </div>

        <!-- Виды услуг -->
        <div>
          <label class="block text-xs md:text-sm font-medium text-gray-700 mb-2">Виды услуг</label>
          <div class="space-y-2 max-h-32 overflow-y-auto">
            <label v-for="service in availableServices" :key="service.value" class="flex items-center">
              <input
                type="checkbox"
                :value="service.value"
                v-model="filters.services"
                class="rounded"
              />
              <span class="ml-2 text-xs md:text-sm text-gray-700">{{ service.label }}</span>
            </label>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Список экспертов -->
    <div class="flex-1 overflow-y-auto scroll-styling">
      <div class="p-3 md:p-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-6">
        <router-link
          v-for="expert in filteredExperts"
          :key="expert.id"
          :to="{ name: 'PageContentDetailExperts', params: { slug: expert.id } }"
          class="bg-white shadow rounded-lg p-3 md:p-6 flex flex-col gap-3 md:gap-4 hover:shadow-md transition duration-200 cursor-pointer no-underline text-current"
        >
          <!-- Информация о эксперте -->
          <div class="flex items-start gap-3">
            <img
              v-if="expert.photo"
              :src="expert.photo"
              class="w-16 md:w-20 h-16 md:h-20 object-cover rounded-full border shadow flex-shrink-0"
              alt="Фото эксперта"
            />
            <div class="flex-1 min-w-0">
              <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                <h2 class="text-base md:text-lg font-semibold truncate">{{ expert.fio }}</h2>
                <span v-if="expert.is_pilot" class="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0">Пилот</span>
              </div>
              <p class="text-xs md:text-sm text-gray-600 mb-1 truncate">{{ expert.specialization }}</p>
              <p v-if="expert.is_pilot && expert.pilot_category" class="text-xs text-blue-600 mb-1">
                {{ getPilotCategoryLabel(expert.pilot_category) }}
              </p>
              <p class="text-xs md:text-sm text-gray-700 line-clamp-2">
                {{ expert.preview_text }}
              </p>
            </div>
          </div>

          <div v-if="expert.is_pilot && getPilotServiceLabels(expert).length" class="border-t pt-2">
            <p class="text-xs font-medium text-gray-900 mb-1">Виды услуг пилота</p>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="label in getPilotServiceLabels(expert)"
                :key="label"
                class="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded"
              >{{ label }}</span>
            </div>
          </div>

          <!-- Рейтинг -->
          <div class="border-t pt-2 md:pt-3">
            <p class="text-xs text-gray-900 font-medium mb-1 md:mb-2">
              Оценка: {{ expert.rating?.toFixed(1) || '—' }} ⭐
            </p>
            <div
              v-if="!isModerator"
              class="flex items-center gap-1"
              @mouseenter="hoveredExpertId = expert.id"
              @mouseleave="hoveredExpertId = null"
            >
              <div class="flex gap-0.5">
                <img
                  v-for="i in 5"
                  :key="i"
                  style="width: 18px; height: auto;"
                  :src="getDisplayedRating(expert) >= i ? activeIcon : defaultIcon"
                  class="w-5 h-5 cursor-pointer transition-transform duration-150 hover:scale-110"
                  @click.stop.prevent="setRating(expert.id, i)"
                  alt="Оценка"
                />
              </div>
            </div>
          </div>
        </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  HEADERS_TOKEN_JSON,
  API_EXPERTS,
  API_EXPERT_RATING,
  API_PILOT_SERVICES_PUBLIC,
} from "@/const/uri";

export default {
  data() {
    return {
      experts: [],
      ratings: {},
      hoveredExpertId: null,
      searchQuery: "",
      filters: {
        pilotStatus: '',
        pilotCategory: '',
        services: [],
      },
      availableServices: [],
      defaultIcon: "/assets/images/aeroplane.png",
      activeIcon: "/assets/images/aroplane-active.png",
      showFilters: false,
      isMobile: window.innerWidth < 768,
    };
  },
  computed: {
    isModerator() {
      return this.$store.state.user?.role?.key === 3;
    },
    filteredExperts() {
      return this.experts.filter((expert) => {
        // Фильтр по поиску
        const q = this.searchQuery.trim().toLowerCase();
        if (q) {
          const matchesSearch = (
            expert.fio?.toLowerCase().includes(q) ||
            expert.specialization?.toLowerCase().includes(q)
          );
          if (!matchesSearch) return false;
        }

        // Фильтр по статусу пилота
        if (this.filters.pilotStatus === 'pilot' && !expert.is_pilot) {
          return false;
        }
        if (this.filters.pilotStatus === 'non-pilot' && expert.is_pilot) {
          return false;
        }

        // Фильтр по категории пилота
        if (this.filters.pilotCategory && expert.pilot_category !== this.filters.pilotCategory) {
          return false;
        }

        // Фильтр по видам услуг (если выбраны хотя бы один сервис)
        if (this.filters.services.length > 0) {
          const expertServices = expert.pilot_services || [];
          const hasService = this.filters.services.some((service) =>
            expertServices.includes(service)
          );
          if (!hasService) return false;
        }

        return true;
      });
    },
  },
  mounted: async function () {
    await this.fetchPilotServices();
    await this.fetchExperts();
    await this.fetchMyRatings();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    getPilotServiceLabels(expert) {
      const services = expert.pilot_services || [];
      return services
        .map((value) => this.availableServices.find((s) => s.value === value)?.label || value)
        .filter(Boolean);
    },
    getPilotCategoryLabel(category) {
      const labels = {
        first: 'Первая категория',
        second: 'Вторая категория',
        third: 'Третья категория',
        fourth: 'Четвёртая категория',
      };
      return labels[category] || category;
    },
    async fetchExperts() {
      try {
        const res = await axios.get(API_EXPERTS.public, {
          headers: HEADERS_TOKEN_JSON(),
        });
        if (res.data.code === 0) {
          this.experts = res.data.data;
          await this.fetchMyRatings();
        }
      } catch (e) {
        console.error("Ошибка загрузки экспертов:", e);
      }
    },
    async fetchMyRatings() {
      try {
        const res = await axios.get(API_EXPERT_RATING.list, {
          headers: HEADERS_TOKEN_JSON(),
        });
        if (res.data.code === 0) {
          for (let item of res.data.data) {
            this.ratings[item.expert] = item.value;
          }
        }
      } catch (e) {
        console.error("Ошибка загрузки оценок:", e);
      }
    },
    setRating(expertId, value) {
      this.ratings[expertId] = value;
      this.submitRating(expertId, value);
    },
    async submitRating(expertId, value) {
      try {
        await axios.post(
          API_EXPERT_RATING.list,
          { expert: expertId, value },
          { headers: HEADERS_TOKEN_JSON() }
        );
        await this.fetchExperts();
      } catch (e) {
        const errors = e.response?.data?.errors;
        if (errors?.expert) {
          alert(errors.expert[0]);
        } else {
          alert("Ошибка сохранения оценки");
        }
        delete this.ratings[expertId];
      }
    },
    getDisplayedRating(expert) {
      return this.hoveredExpertId === expert.id
        ? this.ratings[expert.id] || 0
        : expert.rating || 0;
    },
    async fetchPilotServices() {
      try {
        const res = await axios.get(API_PILOT_SERVICES_PUBLIC.list, {
          headers: HEADERS_TOKEN_JSON(),
        });
        if (res.data.code === 0) {
          this.availableServices = res.data.data.map(service => ({
            value: service.key,
            label: service.name,
          }));
        }
      } catch (e) {
        console.error("Ошибка загрузки видов услуг:", e);
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth < 768;
      if (!this.isMobile) {
        this.showFilters = false;
      }
    },
  },
};
</script>

<style scoped>
img:hover {
  transform: scale(1.15);
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
